import { Component, OnInit, ViewChild } from '@angular/core';
import { Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { DashboardService } from '../../../dashboard/services/dashboard.service';
import { DialogService } from '../../../common/services/dialog.service';
import { FlowInstanceService } from '../../../flow/services/flow-instance.service';
import { LoaderService } from '../../../common/services/loader.service';
import { ModuleGroupService } from '../../../common/services/modulegroup.service';
import { SidePanelService } from '../../../common/services/side-panel.service';
import { SubHeaderService } from '../../../core/services/sub-header.service';

import { Module } from '../../../common/models/module.model';
import { ModuleGroup } from '../../../common/models/modulegroup.model';
import { NavigationReference } from '../../../common/models/navigationreference.model';
import { Claim } from "../../models/claim.model";
import { ReferenceType } from '../../../common/models/referencetype.enum';
import { SubHeader } from "../../../core/models/sub-header.model";
import { SidePanel } from '../../../common/models/side-panel.model';
import { LunaClaimTypes } from "../../../models/user-identity.model";

@Component({
    selector: 'manage-modules',
    templateUrl: './manage-modules.component.html',
    styleUrls: ['./manage-modules.component.scss'],
    providers: [DialogService],
    standalone: false
})
export class ManageModulesComponent implements OnInit {

  public _currentModuleGroup: ModuleGroup;
  public _sidePanelAddLink: SidePanel = new SidePanel({
    tabs: [{ title: "Add Navigation Item" }],
    sections: [{ componentName: 'AddLink', method: (event) => this.saveLink(event), componentData: {} }],
    visible: false
  });
  public _sidePanelAddModule: SidePanel = new SidePanel({
    tabs: [{ title: "Add Navigation Item" }],
    sections: [{ componentName: 'AddNavigationItem', method: (event) => this.saveModuleOrGroup(event), componentData: { moduleGroup: false } }],
    visible: false
  });
  public _subHeader: SubHeader = new SubHeader({ title: "Manage Module Groups" });
  public curModule: number = 0;
  public editingModuleGroupId: string = '';
  public editingModuleId: string = '';
  public moduleGroups: Array<ModuleGroup> = new Array<ModuleGroup>();
  public referenceType: any = ReferenceType;
  public moduleGroup: ModuleGroup;
  public module: Module;
  public navigationItem: any;
  public type: any;

  constructor(
    public subHeaderService: SubHeaderService,
    public loaderService: LoaderService,
    public moduleGroupService: ModuleGroupService,
    public dialogService: DialogService,
    public sidePanelService: SidePanelService,
    public dashboardService: DashboardService,
    public flowService: FlowInstanceService
  ) { }

  ngOnInit() {
    this.subHeaderService.updateSubHeader(this._subHeader);
    this.loaderService.toggleLoader(true);
    this.moduleGroupService.getAll().subscribe(result => {
      this.moduleGroups = result;
      this.loaderService.toggleLoader(false);
    });
  }

  public deleteModule(moduleGroup: ModuleGroup, module: Module): void {
    this.moduleGroup = moduleGroup;
    this.module = module;
    this.dialogService.openDialog("yesNo", `Deleting the ${module.title} module will remove all associated data.<br>Are you sure you want to delete the ${module.title} module?`, "deleteModule");
  }

  public deleteModuleGroup(moduleGroup: ModuleGroup): void {
    this.moduleGroup = moduleGroup;
    this.dialogService.openDialog("yesNo", `Deleting this ${moduleGroup.title} module group will remove all associated data.<br>Are you sure you want to delete the ${moduleGroup.title} module group?`, "deleteModuleGroup");
  }

  public deleteNavigationItem(moduleGroup: ModuleGroup, module: Module, navigationItem): void {
      this.moduleGroup = moduleGroup;
    this.module = module;
    this.navigationItem = navigationItem;
    this.type = this.getNavigationTypeDescription(navigationItem.referenceType);
    this.dialogService.openDialog("yesNo", `Deleting the ${navigationItem.title} ${this.type} will remove all associated data.<br>Are you sure you want to delete the ${navigationItem.title} ${this.type}?`, "deleteNavigationItem");
  }

  public editModuleGroupName(group: ModuleGroup): void {
    this.editingModuleGroupId = group.id;
  }

  public editModuleName(module: Module): void {
    this.editingModuleId = module.id;
  }

  public getNavigationTypeDescription(type: ReferenceType): string {
    let description: string;
    switch (type) {
      case ReferenceType.Navigation: {
        description = 'link';
        break;
      }
      case ReferenceType.Dashboard: {
        description = 'dashboard';
        break;
      }
      case ReferenceType.Workflow: {
        description = 'workflow';
        break;
      }
    };
    return description;
  }

  public saveLink(data: any): void {
    this.loaderService.toggleLoader(true);
    const link: NavigationReference = {
      id: '',
      referenceType: ReferenceType.Navigation,
      showEditWorkflow: false,
      title: data.title,
      route: data.route
      };
      if (data.permission != null && data.permission.length > 0) {
          link.requiredClaims = [];
          const claim: Claim = new Claim();
          claim.type = LunaClaimTypes.System;
          claim.value = data.permission;
          link.requiredClaims.push(claim);
      }
    this.moduleGroupService.createReference(data.moduleGroupId, data.moduleId, link).subscribe(res => {
      const index = this._currentModuleGroup.modules.findIndex(m => m.id === data.moduleId);
      this._currentModuleGroup.modules[index].navigationItems.push(res);
      this.loaderService.toggleLoader(false);
    }, error => console.log(error))
  }

  public saveModule(title: string): void {
    this.moduleGroupService.createModule(this._currentModuleGroup.id, { title: title, displayOrder: this._currentModuleGroup.modules.length } as Module).subscribe(module => {
      this._currentModuleGroup.modules.push(module);
      this._currentModuleGroup = undefined;
      this.sidePanelService.toggleSidePanel(false);
      this.loaderService.toggleLoader(false);
    }, error => console.log(error));
  }

  public saveModuleGroup(title: string): void {
    this.moduleGroupService.postModuleGroup({ title: title } as ModuleGroup).subscribe(moduleGroup => {
      this.moduleGroups.push(moduleGroup);
      this.sidePanelService.toggleSidePanel(false);
      this.loaderService.toggleLoader(false);
    }, error => console.log(error));
  }

  public saveModuleOrGroup(data: any): void {
    this.loaderService.toggleLoader(true);
    if (data.type === 'module')
      this.saveModule(data.title);
    else if (data.type === 'moduleGroup')
      this.saveModuleGroup(data.title);
  }

  public setIsVisible(event: any, item: NavigationReference, moduleGroup: ModuleGroup): void {
    item.showInNavigation = event.currentTarget.checked;
    this.updateModuleGroup(moduleGroup);
  }

  public showAddLinkPanel(module: Module, moduleGroup: ModuleGroup): void {
    this.sidePanelService.newSidePanel(this._sidePanelAddLink);
    this.sidePanelService.updateSectionData('AddLink', { module: module, moduleGroupId: moduleGroup.id });
    this.sidePanelService.toggleSidePanel(true);
    if (moduleGroup) this._currentModuleGroup = moduleGroup;
  }

  public showAddModulePanel(moduleGroup: ModuleGroup): void {
    this.sidePanelService.newSidePanel(this._sidePanelAddModule);
    this.sidePanelService.updateSectionData('AddNavigationItem', { moduleGroup: moduleGroup });
    this.sidePanelService.toggleSidePanel(true);
    if (moduleGroup) this._currentModuleGroup = moduleGroup;
  }

  public updateModuleGroup(group: ModuleGroup): void {
    this.loaderService.toggleLoader(true);
    this.moduleGroupService.postModuleGroup(group).subscribe(res => {
      this.loaderService.toggleLoader(false);
      this.editingModuleGroupId = '';
      this.editingModuleId = '';
    });
  }

  public onDialogConfirm(): void {
    this.dialogService.closeDialog();
    if (this.dialogService.dialogId === 'deleteModule') {
      this.loaderService.toggleLoader(true);
      this.moduleGroupService.deleteModule(this.moduleGroup.id, this.module.id).subscribe(success => {
        let index = this.moduleGroup.modules.findIndex(x => x.id === this.module.id);
        this.moduleGroup.modules.splice(index, 1);
        this.loaderService.toggleLoader(false);
      }, error => console.log(error));
    } else if (this.dialogService.dialogId === 'deleteModuleGroup') {
      this.moduleGroupService.deleteModuleGroup(this.moduleGroup.id).subscribe(success => {
        let index = this.moduleGroups.findIndex(x => x.id === this.moduleGroup.id);
        this.moduleGroups.splice(index, 1);
        this.loaderService.toggleLoader(false);
      });
    } else if (this.dialogService.dialogId === 'deleteNavigationItem') {
      this.loaderService.toggleLoader(true);
      let obs: Observable<any> = null;
      switch (this.type) {
        case 'dashboard':
          obs = this.dashboardService.delete(this.navigationItem.uriComponent);
          break;
        case 'workflow':
          obs = this.flowService.deleteTemplate(this.navigationItem.uriComponent);
          break;
        case 'link':
          obs = this.moduleGroupService.deleteReference(this.moduleGroup.id, this.module.id, this.navigationItem.id);
          break;
        default:
          console.error("Nav Type not handled in manage-modules");
          break;
      }
      obs.subscribe(() => {
        let index = this.module.navigationItems.findIndex(x => x.id === this.navigationItem.id);
        this.module.navigationItems.splice(index, 1);
        this.loaderService.toggleLoader(false);
      });
    }
  }

  public onDialogClose(): void {
    this.dialogService.closeDialog();
    this.loaderService.toggleLoader(false);
  }

}
